import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Lock,
  Unlock,
  DollarSign,
  Users,
  TrendingUp,
  BarChart3,
  Eye,
  Loader2,
} from 'lucide-react';

const LOCK_TYPE_LABELS = {
  follow: 'Follow',
  pay: 'Pay',
  engage: 'Engage',
  subscribe: 'Subscribe',
  custom: 'Custom',
};

const LOCK_TYPE_COLORS = {
  follow: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  pay: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  engage: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  subscribe: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  custom: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
};

export default function ContentLockDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/content-locks/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchData(); else setLoading(false); }, [token, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="content-lock-dashboard" className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            CONTENT LOCKS
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Track your exclusive content performance</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-muted/30 border-border p-4">
            <Lock className="w-4 h-4 text-violet-400 mb-2" />
            <p className="text-xl font-bold">{data?.total_locked_posts || 0}</p>
            <p className="text-[11px] text-muted-foreground">Locked Posts</p>
          </Card>
          <Card className="bg-muted/30 border-border p-4">
            <Unlock className="w-4 h-4 text-cyan-400 mb-2" />
            <p className="text-xl font-bold">{data?.total_unlocks || 0}</p>
            <p className="text-[11px] text-muted-foreground">Total Unlocks</p>
          </Card>
          <Card className="bg-muted/30 border-border p-4">
            <DollarSign className="w-4 h-4 text-emerald-400 mb-2" />
            <p className="text-xl font-bold">${data?.total_revenue || 0}</p>
            <p className="text-[11px] text-muted-foreground">Revenue</p>
          </Card>
          <Card className="bg-muted/30 border-border p-4">
            <TrendingUp className="w-4 h-4 text-amber-400 mb-2" />
            <p className="text-xl font-bold">
              {data?.posts?.length > 0
                ? Math.round(data.posts.reduce((s, p) => s + p.conversion_rate, 0) / data.posts.length)
                : 0}%
            </p>
            <p className="text-[11px] text-muted-foreground">Avg Conversion</p>
          </Card>
        </div>

        {/* Posts */}
        {data?.posts?.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Your Locked Posts</h2>
            {data.posts.map(p => (
              <Card key={p.post_id} className="bg-muted/20 border-border p-4" data-testid={`lock-post-${p.post_id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{p.content || 'Untitled'}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <Badge className={LOCK_TYPE_COLORS[p.lock_type] || 'bg-muted/50 text-muted-foreground'}>
                        <Lock className="w-3 h-3 mr-1" />
                        {LOCK_TYPE_LABELS[p.lock_type] || p.lock_type}
                        {p.price ? ` $${p.price}` : ''}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Unlock className="w-3 h-3" />{p.unlocks} unlocks</span>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />{p.views} views</span>
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1"><BarChart3 className="w-3 h-3" />{p.conversion_rate}% conv.</span>
                      {p.revenue > 0 && <span className="text-[11px] text-emerald-400 flex items-center gap-1"><DollarSign className="w-3 h-3" />${p.revenue}</span>}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-muted/30 border-border p-10 text-center">
            <Lock className="w-12 h-12 text-muted-foreground/80 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-foreground mb-1">No Locked Content Yet</h3>
            <p className="text-sm text-muted-foreground">Create a post and add a content lock to start monetizing your exclusive content.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
